import { MilestoneMap1, MilestoneMapInterval } from '../types';
import {
  standardizeStartDate,
  getIntervalEnd,
  standardizeToQuarterStart,
  standardizeToQuarterEnd,
} from './intervalUtils';
import { ZoneOptions, cloneInZone, toPlainDate } from './zone';

/**
 * Generates time intervals for a milestone map.
 * @param map - The milestone map
 * @param intervalOverride - Optional interval to use instead of map.interval (for zoom functionality)
 * @param options - Optional `{ timeZone }` (IANA). Omitted: runtime-local zone, as the web does today.
 * @returns Array of interval objects with start and end dates
 */
export function getIntervalsForMilestoneMap(
  map: MilestoneMap1,
  intervalOverride?: MilestoneMapInterval,
  options: ZoneOptions = {},
): Array<{ start: Date; end: Date }> {
  const startDate = new Date(map.startDate);
  const endDate = new Date(map.endDate);
  const intervals: Array<{ start: Date; end: Date }> = [];
  const tz = options.timeZone;
  const intervalType = intervalOverride || map.interval;

  switch (intervalType) {
    case 'hours': {
      let current = cloneInZone(standardizeStartDate(startDate, 'hours', options), tz);
      while (current <= endDate) {
        const hourStart = toPlainDate(cloneInZone(current, tz), tz);
        const hourEnd = getIntervalEnd(hourStart, 'hours', true, options);
        intervals.push({ start: hourStart, end: hourEnd });
        current.setHours(current.getHours() + 1);
      }
      break;
    }
    case 'days': {
      let current = cloneInZone(standardizeStartDate(startDate, 'days', options), tz);
      while (current <= endDate) {
        const dayStart = toPlainDate(cloneInZone(current, tz), tz);
        const dayEnd = getIntervalEnd(dayStart, 'days', true, options);
        intervals.push({ start: dayStart, end: dayEnd });
        current.setDate(current.getDate() + 1);
      }
      break;
    }
    case 'weeks': {
      // TODO: Review/Revise in the future to allow flexibility
      // for timing week start/end with sprint start/end dates
      // (e.g., sprint starts on Wednesday, ends on Tuesday).
      // For now, we're standardizing the start date to the beginning
      // of the week, like the other intervals.
      let current = cloneInZone(standardizeStartDate(startDate, 'weeks', options), tz);
      while (current <= endDate) {
        const weekStart = toPlainDate(cloneInZone(current, tz), tz);
        const weekEnd = getIntervalEnd(weekStart, 'weeks', true, options);
        intervals.push({ start: weekStart, end: weekEnd });
        current.setDate(current.getDate() + 7);
      }
      break;
    }
    case 'months': {
      let current = cloneInZone(standardizeStartDate(startDate, 'months', options), tz);
      while (current <= endDate) {
        const monthStart = toPlainDate(cloneInZone(current, tz), tz);
        const monthEnd = getIntervalEnd(monthStart, 'months', true, options);
        intervals.push({ start: monthStart, end: monthEnd });
        current.setMonth(current.getMonth() + 1);
        current.setDate(1);
      }
      break;
    }
    case 'quarters': {
      // First standardize the start date to the beginning of its quarter
      let current = cloneInZone(standardizeToQuarterStart(startDate, options), tz);
      while (current <= endDate) {
        const quarterStart = toPlainDate(cloneInZone(current, tz), tz);
        // For the end date, we need to look at the quarter start date
        // to determine the correct quarter end
        const quarterEnd = standardizeToQuarterEnd(quarterStart, options);
        intervals.push({ start: quarterStart, end: quarterEnd });
        // Move to start of next quarter
        current.setMonth(current.getMonth() + 3);
        current.setDate(1);
      }
      break;
    }
    case 'years': {
      let current = cloneInZone(standardizeStartDate(startDate, 'years', options), tz);
      while (current <= endDate) {
        const yearStart = toPlainDate(cloneInZone(current, tz), tz);
        const yearEnd = getIntervalEnd(yearStart, 'years', true, options);
        intervals.push({ start: yearStart, end: yearEnd });
        current.setFullYear(current.getFullYear() + 1);
        current.setMonth(0, 1);
      }
      break;
    }
    default:
      throw new Error(
        `Interval '${intervalType}' is not supported. Only 'hours', 'days', 'weeks', 'months', 'quarters', and 'years' are allowed.`,
      );
  }

  return intervals;
}
